import { reactive } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { useDynamicYieldApi } from '~/api/use-dynamic-yield-api';
import { useUserIpApi } from '~/api/use-user-ip-api';
import { CAMPAIGN_STATUS, ENGAGEMENT_TYPES } from '~/constants/campaigns';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as RudderstackReady from '~/lib/rudderstack/rudderstack-ready-api';
import { getExperimentTypeBySelectorName, getMainAnalyticsMetadata } from '~/utils/dynamic-yield';

export const dyStore = reactive({
  campaigns: {
    global: {},
    sections: {},
  },
  isInitialized: false,
});

/**
 * Composable for managing Dynamic Yield campaigns.
 *
 * @returns {import('~/types/dynamic-yield').DynamicYieldCampaignsReturn} An object containing functions and state for working with Dynamic Yield campaigns.
 */
export function useDynamicYieldCampaigns() {
  const { choose, isDynamicYieldInitialized, reportEngagement } = useDynamicYieldApi();
  const { getUserIp } = useUserIpApi();

  /**
   * Helper function to update the campaign state consistently.
   *
   * @param {string} name - The campaign name.
   * @param {import('~/types/dynamic-yield').Campaign} update - The update to merge into the campaign's state.
   * @param {string} [section=null] - Optional section identifier for section-specific campaigns.
   */
  const updateCampaignState = (name, update, section = null) => {
    const isEnabled = update?.variations?.some(
      (variation) => Object.keys(variation?.payload?.data || {}).length,
    );

    const campaignData = { enabled: isEnabled, ...dyStore.campaigns[name], ...update };

    if (section) {
      if (!dyStore.campaigns.sections[section]) {
        dyStore.campaigns.sections[section] = {};
      }
      dyStore.campaigns.sections[section][name] = campaignData;
    } else {
      dyStore.campaigns.global[name] = campaignData;
    }
  };

  /**
   * Send the analytics event for the experiment viewed
   *
   * @param {Object} choice - The choice object returned by Dynamic Yield API /choose endpoint
   * @param {string} selectorName - The name of the campaign selector
   */
  const sendAnalyticsExperimentViewed = (choice, selectorName) => {
    const analyticsMetadata = getMainAnalyticsMetadata(choice);
    if (analyticsMetadata) {
      const experimentType = getExperimentTypeBySelectorName(selectorName);

      // Send the analytics event for the experiment viewed
      RudderstackReady.rudderanalyticsReadyCallback(() => {
        AnalyticsData.triggerExperimentViewed(analyticsMetadata, experimentType);
      }, RudderstackReady.CALLBACK_NAMES.USE_DYNAMIC_YIELD_CAMPAIGNS);
    } else {
      DataDogLogs.error(
        `[Allbirds composable] -  useDynamicYieldCampaigns - sendAnalyticsExperimentViewed - no analytics metadata for campaign ${selectorName}`,
        { choice, selectorName },
        new Error('No analytics metadata for campaign'),
      );
    }
  };

  /**
   * Retrieves all campaigns.
   *
   * @param {string} [section=null] - Optional section identifier to get section-specific campaigns.
   * @returns {Object.<string, import('~/types/dynamic-yield').Campaign>} A map of campaign names to their data.
   */
  const getCampaigns = (section = null) => {
    if (section) {
      return dyStore.campaigns.sections[section] || {};
    }

    return dyStore.campaigns.global;
  };

  /**
   * Retrieves a campaign by its name.
   *
   * @param {string} name - The campaign name.
   * @param {string} [section=null] - Optional section identifier for section-specific campaign.
   * @returns {import('~/types/dynamic-yield').Campaign|null} The campaign object, or null if not found.
   */
  const getCampaign = (name, section = null) => {
    if (section) {
      return dyStore.campaigns.sections[section]?.[name] || null;
    }
    return dyStore.campaigns.global[name] || null;
  };

  /**
   * Retrieves variation data for a given campaign and variation index.
   *
   * @param {import('~/types/dynamic-yield').Campaign} campaign - The campaign object.
   * @param {number} [variationIndex=0] - The index of the variation to retrieve.
   * @returns {import('~/types/dynamic-yield').CampaignVariation|null} The variation data with decisionId included, or null if not found.
   */
  const getCampaignVariationData = (campaign, variationIndex = 0) => {
    const variation = campaign?.variations?.[variationIndex];

    return variation ? { ...variation, decisionId: campaign.decisionId } : null;
  };

  /**
   * Reports an engagement event (impression, click, etc.) on a campaign variation to Dynamic Yield.
   *
   * @param {Object} options - The engagement options.
   * @param {string} options.campaignName - The name of the campaign.
   * @param {string} [options.engagementType=ENGAGEMENT_TYPES.CLICK] - The type of engagement (CLICK, IMP, SLOT_CLICK, etc.).
   * @param {string} [options.slotId] - The slot ID for slot-specific engagements.
   * @param {string} [options.section] - The section identifier for section-specific campaigns.
   * @returns {Promise<import('~/types/dynamic-yield').ClickResponse>} A promise that resolves with the response from the engagement reporting.
   */
  const reportCampaignEngagement = async ({
    campaignName,
    engagementType = ENGAGEMENT_TYPES.CLICK,
    section,
    slotId,
  }) => {
    const campaign = getCampaign(campaignName, section);

    if (!campaign || campaign.status !== CAMPAIGN_STATUS.SUCCESS) {
      return { success: false };
    }

    const variation = campaign?.variations?.[0];

    if (!Object.keys(variation).length) return { success: false };

    if (!campaign.decisionId) {
      DataDogLogs.info(
        '[Allbirds API] - DynamicYield - Skipping engagement: no decisionId available',
        {
          campaign,
          campaignName,
          engagementType,
        },
      );
      return { skipped: true, success: false };
    }

    const engagement = [
      {
        decisionId: campaign.decisionId,
        type: engagementType,
      },
    ];

    // Add slotId for slot-specific click engagements
    if (engagementType === ENGAGEMENT_TYPES.SLOT_CLICK && slotId) {
      engagement[0].slotId = slotId;
    }

    // Add slotIds array for slot-specific impressions
    if (engagementType === ENGAGEMENT_TYPES.SLOT_IMPRESSION) {
      // For slot impressions, we need to extract slotIds from the campaign data
      const slotIds =
        variation.payload?.data?.slots?.map((slot) => slot.slotId).filter(Boolean) || [];
      if (slotIds.length > 0) {
        engagement[0].slotIds = slotIds;
      } else {
        DataDogLogs.info(
          '[Allbirds API] - DynamicYield - Skipping slot impression: no valid slotIds found',
          {
            campaignName,
            variation: variation?.payload?.data,
          },
        );
        return { skipped: true, success: false };
      }
    }

    try {
      return await reportEngagement(engagement);
    } catch (error) {
      DataDogLogs.info(
        `[Allbirds composable] - useDynamicYieldCampaigns - reportCampaignEngagement - error reporting engagement for campaign ${campaignName} (likely blocked by browser):`,
        { campaignName, error: error.message },
      );
      return { error: error.message, success: false };
    }
  };

  /**
   * Transforms a campaign's variation data using an optional transformer function.
   *
   * @param {import('~/types/dynamic-yield').Campaign} campaign - The campaign object.
   * @param {number} [variationIndex=0] - The index of the variation to transform.
   * @param {Function} [transformer=null] - An optional transformer function to apply to the variation data.
   * @returns {Object|null} The transformed campaign data or null if the variation data is not found.
   */
  const transformCampaignData = (campaign, variationIndex = 0, transformer = null) => {
    const variationData = getCampaignVariationData(campaign, variationIndex);

    if (!variationData) return null;
    return typeof transformer === 'function'
      ? transformer(variationData)
      : variationData.payload?.data || variationData;
  };

  /**
   * Initializes multiple campaigns by their selector names with a single network request.
   *
   * @param {Array<Object>} campaigns - An array of campaign objects, each object contains a selectorName property and experimentType property.
   * @param {Object} [options={ useIp: true, timeoutMs: 8000 }] - Additional options for the campaign fetch.
   * @returns {Promise<void>} A promise that resolves when all campaigns have been initialized.
   */
  const initializeCampaigns = async (campaigns, options = {}) => {
    if (!isDynamicYieldInitialized()) {
      DataDogLogs.error(
        '[Allbirds composable] -  useDynamicYieldCampaigns - initializeCampaigns - not initialized when initializing campaigns',
        { campaigns, options },
        new Error('not initialized when initializing campaigns'),
      );
      return;
    }
    dyStore.isInitialized = true;

    // Normalize input to an array of selector names
    const selectors = campaigns.map((item) =>
      typeof item === 'string' ? item : item.selectorName,
    );

    if (selectors.length === 0) return;

    // Set default timeout of 8 seconds
    const timeoutMs = options.timeoutMs || 8000;

    try {
      const userIp = await getUserIp();

      // Create a promise that rejects after the timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Campaign fetch timed out')), timeoutMs);
      });

      // Race the API call against the timeout
      const response = await Promise.race([choose({ names: selectors }, userIp), timeoutPromise]);

      if (response.error) {
        throw new Error(response.error);
      }

      // Track which selectors were successfully processed
      const processedSelectors = new Set();

      // Process each choice from the response and update campaign state
      if (response.choices && response.choices.length) {
        response.choices.forEach((choice) => {
          if (!choice || !choice.variations?.length) return;

          const selectorName =
            selectors.find((selector) => choice.name === selector) || choice.name;

          processedSelectors.add(selectorName);

          const campaignData = {
            choiceId: choice.id,
            decisionId: choice.decisionId,
            selectorName,
            status: CAMPAIGN_STATUS.SUCCESS,
            variations: choice.variations,
          };

          updateCampaignState(selectorName, campaignData);

          // Send the analytics event
          sendAnalyticsExperimentViewed(choice, selectorName);
        });
      }

      // Mark any selectors that weren't processed as empty with ERROR status
      selectors.forEach((selectorName) => {
        if (!processedSelectors.has(selectorName)) {
          updateCampaignState(selectorName, {
            error: 'No campaign data returned from Dynamic Yield',
            status: CAMPAIGN_STATUS.ERROR,
          });
        }
      });
    } catch (error) {
      // Mark all campaigns as failed if the batch request fails
      selectors.forEach((selectorName) => {
        updateCampaignState(selectorName, {
          error: error.message,
          status: CAMPAIGN_STATUS.ERROR,
        });
      });
      // Use warn for timeouts, error for other issues
      const isTimeout = error.message === 'Campaign fetch timed out';
      const logLevel = isTimeout ? DataDogLogs.warn : DataDogLogs.error;

      logLevel(
        '[Allbirds composable] -  useDynamicYieldCampaigns - initializeCampaigns - error fetching campaigns:',
        { campaigns, isTimeout, options },
        error,
      );
    }
  };

  return {
    campaigns: dyStore.campaigns,
    ENGAGEMENT_TYPES,
    getCampaign,
    getCampaigns,
    initializeCampaigns,
    reportCampaignEngagement,
    transformCampaignData,
  };
}
